/* ==========================================================================
   VoicePad gadget — key legend enhancements.
   Runs AFTER vpg.js. On every device key:
     1. If the legend text matches a known app → show its brand logo on a
        white chip, hide the text.
     2. Else if it matches a known function → render a small inline icon
        above the text (icon stays small, text stays visible).
     3. Else just show the text.
   When the legend changes (vpg.js swapping setups): the OLD label visibly
   dissipates into particles, the NEW label (icon + text, or logo) fades in.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Brand/app → asset filename (shown as full logo chip, text hidden). */
  var LOGO_MAP = {
    chatgpt:    'vpg-logo-chatgpt.svg',
    claude:     'vpg-logo-claude.svg',
    gemini:     'vpg-logo-gemini.svg',
    grok:       'vpg-logo-grok.svg',
    figma:      'vpg-logo-figma.svg',
    zoom:       'vpg-logo-zoom.svg',
    salesforce: 'vpg-logo-salesforce.svg',
    slack:      'vpg-logo-slack.svg',
    gmail:      'vpg-logo-gmail.svg',
    notion:     'vpg-logo-notion.svg',
    youtube:    'vpg-logo-youtube.svg',
    instagram:  'vpg-logo-instagram.svg',
    jira:       'vpg-logo-jira.svg'
  };

  /* Function word → inline SVG path(s) (small icon, text stays).
     Lines are drawn with currentColor, 1.8 stroke, 24×24. */
  var ICON_MAP = {
    agent:          '<path d="M4 20L14 10M12.6 8.6l2.8 2.8M18 3v4M16 5h4M20 10v3M18.5 11.5h3M10 3v2M9 4h2"/>',
    translate:      '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3z"/>',
    meeting:        '<path d="M20 11.5a8 8 0 0 1-11.7 7.1L4 19.8l1.2-4A8 8 0 1 1 20 11.5z"/>',
    meetingnotes:   '<path d="M6 3h8.5L19 7.5V21H6zM14 3v5h5M9 12h6M9 16h6M9 20h4"/>',
    calendar:       '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
    slides:         '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8.5 20h7M12 16v4"/>',
    aiworkspace:    '<path d="M4 20L14 10M12.6 8.6l2.8 2.8M18 3v4M16 5h4M20.5 10.5v2.5M19.25 11.75h2.5M10 3.5v2M9 4.5h2"/>',
    mode:           '<rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="16" cy="12" r="3" fill="currentColor" stroke="none"/>',
    approve:        '<circle cx="12" cy="12" r="9"/><path d="M8 12.5l3 3 5-6"/>',
    reject:         '<circle cx="12" cy="12" r="9"/><path d="M8.5 8.5l7 7M15.5 8.5l-7 7"/>',
    speaktheprompt: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M3 10v4M21 10v4M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
    memo:           '<path d="M6 3h8.5L19 7.5V21H6zM14 3v5h5M9 12h6M9 16h6"/>',
    dictate:        '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
    rewrite:        '<path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4"/>',
    polish:         '<path d="M6 18L18 6M9 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1zM17 14l.5 1.5L19 16l-1.5.5L17 18l-.5-1.5L15 16l1.5-.5z"/>',
    terminal:       '<rect x="3" y="4" width="18" height="16" rx="3"/><path d="M7.5 9.5l3 2.5-3 2.5M12.5 15h4"/>',
    pushtotalk:     '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M3 10v4M21 10v4"/>',
    handsfree:      '<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="6.5" rx="1.6"/><rect x="17" y="14" width="4" height="6.5" rx="1.6"/>',
    summarize:      '<path d="M4 6h16M4 11h16M4 16h10"/>',
    standup:        '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><circle cx="18" cy="9" r="2.5"/><path d="M16 20c0-2.5 1-4 2.5-4s2.5 1.5 2.5 4"/>',
    action:         '<path d="M5 12l4.5 4.5L19 7"/>',
    script:         '<path d="M6 3h12v18H6zM9 7h6M9 11h6M9 15h4"/>',
    caption:        '<path d="M4 5h16v10H4zM7 10h3M12 10h3M9 20l3-3 3 3"/>',
    lecture:        '<path d="M12 3L2 9l10 6 10-6zM6 10v6a6 3 0 0 0 12 0v-6"/>',
    hero:           '<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/>'
  };

  /* Resolve asset URLs from existing <img>s on the page. */
  var ASSET_BASE = null;
  function resolveLogoUrl(file) {
    if (ASSET_BASE) return ASSET_BASE + file;
    var sample = document.querySelector('img[src*="vpg-logo-"]');
    if (sample) {
      var m = sample.src.match(/^(.*\/)vpg-logo-[^\/]+$/);
      if (m) { ASSET_BASE = m[1]; return ASSET_BASE + file; }
    }
    return '/cdn/shop/t/19/assets/' + file;
  }

  function makeIconSvg(d) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = d;
    return svg;
  }

  function apply(key, text) {
    var legend = key.querySelector('[data-legend]');
    var logo = key.querySelector('[data-key-logo]');
    var iconHost = key.querySelector('[data-key-icon]');
    if (!legend || !logo) return;
    var slug = (text || '').toLowerCase().replace(/\s+/g, '');
    var logoFile = LOGO_MAP[slug];
    var iconPath = ICON_MAP[slug];
    /* Clear previous icon */
    if (iconHost) iconHost.textContent = '';
    if (logoFile) {
      logo.src = resolveLogoUrl(logoFile);
      logo.alt = text;
      logo.hidden = false;
      legend.hidden = false;
      key.classList.add('is-logo');
      key.classList.remove('is-icon');
    } else {
      logo.hidden = true;
      logo.removeAttribute('src');
      legend.hidden = false;
      key.classList.remove('is-logo');
      if (iconPath && iconHost) {
        iconHost.appendChild(makeIconSvg(iconPath));
        key.classList.add('is-icon');
      } else {
        key.classList.remove('is-icon');
      }
    }
  }

  function burst(key) {
    if (reduce) return;
    var host = key.querySelector('[data-key-dust]');
    if (!host) return;
    var n = 16;
    for (var i = 0; i < n; i++) {
      var p = document.createElement('span');
      p.className = 'vpg-key__dust-p';
      var angle = (Math.PI * 2) * (i / n) + Math.random() * 0.4;
      var dist = 22 + Math.random() * 30;
      var dx = Math.cos(angle) * dist;
      var dy = Math.sin(angle) * dist - 12;
      p.style.setProperty('--dx', dx + 'px');
      p.style.setProperty('--dy', dy + 'px');
      p.style.setProperty('--dur', (580 + Math.random() * 320) + 'ms');
      p.style.setProperty('--sz', (3.5 + Math.random() * 3).toFixed(1) + 'px');
      host.appendChild(p);
      p.addEventListener('animationend', function () { this.remove(); });
    }
  }

  function dissipate(key, oldText) {
    if (!oldText || reduce) return;
    var legend = key.querySelector('.vpg-key__legend');
    if (!legend) return;
    var ghost = document.createElement('span');
    ghost.className = 'vpg-key__ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.textContent = oldText;
    legend.appendChild(ghost);
    ghost.addEventListener('animationend', function () { this.remove(); });
  }

  function hookKey(key) {
    var legend = key.querySelector('[data-legend]');
    if (!legend) return;
    apply(key, legend.textContent);
    var last = legend.textContent;
    var mo = new MutationObserver(function () {
      var cur = legend.textContent;
      if (cur === last) return;
      var prev = last;
      last = cur;
      if (!cur) { apply(key, ''); return; }
      dissipate(key, prev);
      burst(key);
      apply(key, cur);
      key.classList.remove('is-enter');
      void key.offsetWidth;
      key.classList.add('is-enter');
    });
    mo.observe(legend, { characterData: true, childList: true, subtree: true });
  }

  /* -------- AI section chat: re-theme per active assistant -------- */
  var AI_META = {
    chatgpt: {
      hello: 'What should we work on?',
      placeholder: 'Work with ChatGPT',
      logo: 'vpg-logo-chatgpt.svg',
      model: 'GPT-5',
      newLabel: 'New chat'
    },
    claude: {
      hello: 'Hello',
      placeholder: 'How can I help you today?',
      logo: 'vpg-logo-claude.svg',
      model: 'Opus 5.5',
      newLabel: 'New'
    },
    gemini: {
      hello: 'Where should we start?',
      placeholder: 'Ask Gemini',
      logo: 'vpg-logo-gemini.svg',
      model: 'Gemini Auto',
      newLabel: 'New chat'
    },
    grok: {
      hello: 'What should we explore?',
      placeholder: 'Type @ to search your apps',
      logo: 'vpg-logo-grok.svg',
      model: 'Expert',
      newLabel: 'Chat'
    }
  };

  function initAiChatSwap() {
    var chat = document.querySelector('[data-vpg-demo="ai"] .vpg-chat');
    if (!chat) return;
    var nameEl = chat.querySelector('[data-ai-name]');
    var brandEl = chat.querySelector('[data-ai-brand]');
    var helloEl = chat.querySelector('[data-hello]');
    var phEl = chat.querySelector('[data-ai-placeholder]');
    var sbNameEl = chat.querySelector('[data-ai-sbname]');
    var newLabelEl = chat.querySelector('[data-ai-newlabel]');
    var modelEl = chat.querySelector('[data-ai-model]');
    if (!nameEl) return;
    function paint() {
      var slug = (nameEl.textContent || '').toLowerCase().replace(/\s+/g, '');
      var meta = AI_META[slug];
      if (!meta) return;
      chat.setAttribute('data-ai-active', slug);
      if (brandEl) {
        brandEl.innerHTML = '';
        var img = document.createElement('img');
        img.src = resolveLogoUrl(meta.logo);
        img.alt = '';
        img.width = 20; img.height = 20;
        brandEl.appendChild(img);
      }
      if (helloEl && !helloEl.classList.contains('is-gone')) helloEl.textContent = meta.hello;
      if (phEl) phEl.textContent = meta.placeholder;
      if (sbNameEl) sbNameEl.textContent = nameEl.textContent;
      if (newLabelEl) newLabelEl.textContent = meta.newLabel;
      if (modelEl) modelEl.textContent = meta.model;
    }
    paint();
    new MutationObserver(paint).observe(nameEl, { characterData: true, childList: true, subtree: true });
  }

  function init() {
    var keys = document.querySelectorAll('.vpg-key[data-key]');
    for (var i = 0; i < keys.length; i++) hookKey(keys[i]);
    initAiChatSwap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
